import React, { FC, useEffect } from "react";
import CircleStatus, { StepStatus } from "./CircleStatus";
import CustomNavLink from "./CustomNavLink";
import { fetchPackageInfo } from "../../../actions/PackageActions";
import { updateAllHarmony } from '../../../actions/HarmonyActions';
import { LoadingBlock } from "../../LoadingBlock";
import { useWolfTracsState } from "../../../hooks/redux/useWolfTracsState";
import { useNumberIdsFromRoute } from "../../../hooks/routing/useNumberIdsFromRoute";
import { useReduxDispatch } from "../../../hooks/redux/useReduxDispatch";
import { EnrollmentPackageId } from "../../../types/api/primaryKeys/PrimaryKey";
import {
  Package,
  PackageStep,
  PackageStepType
} from "../../../types/api/enrollmentPackages/Package";

const stepRoutes = [
  {
    type: PackageStepType.Welcome,
    route: "/welcome",
    isHarmonyStep: false
  },
  {
    type: PackageStepType.EmployeeInfo,
    route: "/employee",
    isHarmonyStep: false
  },
  {
    type: PackageStepType.Dependents,
    route: "/dependents",
    isHarmonyStep: false
  },
  {
    type: PackageStepType.DocumentUpload,
    route: "/documents",
    isHarmonyStep: false
  },
  {
    type: PackageStepType.PlanType,
    route: "/plantypes",
    isHarmonyStep: false
  },
  {
    type: PackageStepType.PlanType,
    route: "/harmony",
    isHarmonyStep: true
  },
  {
    type: PackageStepType.Insurability,
    route: "/insurability",
    isHarmonyStep: false
  },
  {
    type: PackageStepType.Summary,
    route: "/summary",
    isHarmonyStep: false
  },
  {
    type: PackageStepType.Finish,
    route: "/finish",
    isHarmonyStep: false
  }
] as const;

const EnrollmentWizardNavigator: FC = () => {
  const { activePackage } = useWolfTracsState(s => s.packages);
  const { harmonyReset, enrollmentToken, isFetching } = useWolfTracsState(s => s.harmony);
  const { packageId: routePackageId } = useNumberIdsFromRoute() as { packageId: EnrollmentPackageId };

  const dispatch = useReduxDispatch();

  useEffect(() => {
    if (routePackageId) {
      dispatch(fetchPackageInfo(routePackageId));
    }
  }, [routePackageId]);

  useEffect(() => {
    const isHarmonyUpdateNeeded = (activePackage: Package) => {
      const currentStepNumber = activePackage.lastCompletedStepNumber + 1;
      if (enrollmentToken) {
        dispatch(updateAllHarmony(routePackageId, enrollmentToken, activePackage, currentStepNumber));
      } else if (!isFetching) {
        dispatch(updateAllHarmony(routePackageId, null, activePackage, currentStepNumber));
      }
    };
    if (harmonyReset && activePackage.data) {
      isHarmonyUpdateNeeded(activePackage.data);
    }
  }, [activePackage.data, isFetching]);


  const getMatchingRoute = (step: PackageStep) => {
    let route = "";
    stepRoutes.forEach(sr => {
      if (sr.type === step.type) {
        //if it is a plantype
        if (
          step.relatedEntityId &&
          !step.harmonyProductGroup &&
          !sr.isHarmonyStep
        ) {
          route = sr.route + "/" + step.relatedEntityId;
        } else if (step.harmonyProductGroup && sr.isHarmonyStep) {
          route = sr.route + "/" + step.harmonyProductGroup;
        } else if (!sr.isHarmonyStep) {
          route = sr.route;
        }
      }
    });

    return route;
  };

  const getStepState = (step: PackageStep) => {
    // Get the last updated step number.
    const lastCompletedStepNumber =
      activePackage.data?.lastCompletedStepNumber ?? null;

    // By default, the step will be flagged as invalid.
    let stepStatus = StepStatus.Invalid;
    let isValid = false;

    if (lastCompletedStepNumber !== null) {
      // If the current step has already been completed:
      if (step.number <= lastCompletedStepNumber) {
        stepStatus = StepStatus.Completed;
        isValid = true;
      } // If the current step is the highest uncompleted sequential step:
      else if (step.number === lastCompletedStepNumber + 1) {
        stepStatus = StepStatus.Current;
        isValid = true;
      }
    }

    return {
      isValid: isValid,
      status: stepStatus
    };
  };

  const steps = activePackage.data?.steps ?? [];
  const { loading } = activePackage;

  // Short circuit for loading block.
  if (loading) {
    return <LoadingBlock />;
  }

  return (
    <React.Fragment>
        {steps.map(step => {
          const stepState = getStepState(step);
          const linkTo = `/packages/${routePackageId}${getMatchingRoute(step)}`;
          return (
            <li key={`ewn-${step.name}`}>
              <CustomNavLink to={linkTo} valid={stepState.isValid}>
                <CircleStatus stepState={stepState} /> {step.name}
              </CustomNavLink>
            </li>
          );
        })
      }
    </React.Fragment>
  );
};

export default EnrollmentWizardNavigator;
